/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * The category trail's row treatment: one line, with overflow SCROLLED rather than clipped.
 *
 * Exported so the resolved trail and the loading skeleton compose the row identically — the skeleton
 * reserves the height the trail then occupies, which only holds if both build it the same way. Same
 * reasoning as `BREADCRUMB_ENTRY_PADDING`.
 *
 * NOT part of the `ui/breadcrumb` primitive: its default is a WRAPPING list, which is what
 * `_app.about-us` and `_app.account.orders.$orderNo` want and keep. `src/components/ui/**` is also
 * unlinted and re-synced from upstream shadcn.
 *
 * `p-1 -m-1` is ONE unit — never keep the padding without the margin, or vice versa. Overflow clips
 * at the PADDING box and the entries carry no ring of their own, so the 4px buys the UA focus outline
 * room and the equal negative margin hands the space back at zero layout cost.
 *
 * Why the rest — scrolling over truncation, the explicit `overflow-y-hidden`, the hidden scrollbar
 * and the CLS contract behind it:
 * `openspec/changes/archive/2026-08-13-breadcrumb-overflow-scroll/design.md`.
 */
export const BREADCRUMB_SCROLL_ROW =
    'flex-nowrap overflow-x-auto overflow-y-hidden overscroll-x-contain p-1 -m-1 whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';
